import React from 'react';
import { Link } from 'react-router-dom';
import './css/flprivacypolicy.css';
import FLHeader from './FLHeader';

export default function FLTermsOfService() {
    return (
        <div className="fl">
            <FLHeader subtitle="Terms and Conditions" />

            <main className="fl__content">
                <p className="fl__updated">Last Updated: 8 September 2026</p>

                <div className="fl__policy">
                    <p>
                        These Terms &amp; Conditions (&quot;Terms&quot;) govern your use of the
                        FitnessLogger mobile application, website and related services
                        (collectively, the &quot;Service&quot;).
                    </p>
                    <p>
                        FitnessLogger is operated by Solo Pixel Dev (&quot;FitnessLogger&quot;,
                        &quot;we&quot;, &quot;us&quot; or &quot;our&quot;).
                    </p>
                    <p>
                        By creating an account, accessing or using FitnessLogger, you agree
                        to these Terms. If you do not agree with these Terms, you should not
                        use the Service.
                    </p>

                    <section className="fl__section">
                        <h3 className="fl__section-title">1. About FitnessLogger</h3>
                        <p>
                            FitnessLogger is a fitness and training logging application that
                            allows users to record, organise and review information relating
                            to their exercise and training activities.
                        </p>
                        <p>Features may include:</p>
                        <ul className="fl__list">
                            <li>Weight training logs</li>
                            <li>Sports training logs</li>
                            <li>Cardio training logs</li>
                            <li>Core training logs</li>
                            <li>
                                Exercise metrics including sets, repetitions, weight,
                                distance, duration and levels
                            </li>
                            <li>Exercise progression charts</li>
                            <li>Training history</li>
                            <li>Training calendars</li>
                            <li>Workout and exercise notes</li>
                            <li>
                                Other fitness-related tracking features introduced from time
                                to time
                            </li>
                        </ul>
                        <p>
                            FitnessLogger is intended to help users record their own training
                            activities and review their historical performance.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">2. Eligibility</h3>
                        <p>
                            You must be legally permitted to use the Service in your country
                            or jurisdiction.
                        </p>
                        <p>
                            If you are under the applicable age of majority in your
                            jurisdiction, you should only use FitnessLogger with the
                            involvement and permission of a parent or legal guardian where
                            required by law.
                        </p>
                        <p>
                            You are responsible for ensuring that your use of the Service
                            complies with the laws applicable to you.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">3. Creating an Account</h3>
                        <p>
                            Certain FitnessLogger features may require you to create an
                            account.
                        </p>
                        <p>When creating an account, you agree to:</p>
                        <ul className="fl__list">
                            <li>Provide accurate information.</li>
                            <li>Keep your account information up to date.</li>
                            <li>Keep your login credentials secure.</li>
                            <li>Not share your account with another person.</li>
                            <li>
                                Notify us if you believe your account has been accessed
                                without your permission.
                            </li>
                        </ul>
                        <p>
                            You are responsible for activity carried out through your account
                            unless the activity resulted from our failure to maintain
                            appropriate security.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">4. Your Workout and Personal Data</h3>
                        <p>
                            You may enter information into FitnessLogger relating to your
                            training and exercise activities.
                        </p>
                        <p>This may include:</p>
                        <ul className="fl__list">
                            <li>Exercise names</li>
                            <li>Sets and repetitions</li>
                            <li>Weights</li>
                            <li>Distances</li>
                            <li>Duration</li>
                            <li>Training levels</li>
                            <li>Equipment</li>
                            <li>Muscle groups</li>
                            <li>Training dates</li>
                            <li>Exercise notes</li>
                            <li>Other information you voluntarily enter</li>
                        </ul>
                        <p>
                            You retain ownership of the information you submit to
                            FitnessLogger, subject to the rights necessary for us to provide
                            the Service.
                        </p>
                        <p>
                            Our collection and processing of personal information is governed
                            by our{' '}
                            <Link to="/fitnesslogger/policy">Privacy Policy</Link>, which
                            forms part of these Terms.
                        </p>
                        <p>
                            Our Privacy Policy explains what information we collect, how it
                            is used, how it is stored, how it may be shared and how you can
                            request deletion of your information.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">5. Health and Fitness Disclaimer</h3>
                        <p>
                            FitnessLogger is a tracking and record-keeping tool. It is{' '}
                            <strong>not a medical service</strong> and does not provide
                            medical advice, diagnosis, treatment or professional fitness
                            advice.
                        </p>
                        <p>
                            Information displayed by FitnessLogger should not be considered a
                            substitute for advice from a qualified doctor, physiotherapist,
                            sports professional, personal trainer or other appropriately
                            qualified professional.
                        </p>
                        <p>
                            You are responsible for determining whether an exercise or
                            training activity is appropriate for you.
                        </p>
                        <p>
                            Before beginning a new exercise programme or significantly
                            changing your physical activity, you should consult an
                            appropriate healthcare professional where necessary.
                        </p>
                        <p>
                            You should stop exercising and seek appropriate medical attention
                            if you experience pain, dizziness, difficulty breathing, chest
                            pain, injury or other concerning symptoms.
                        </p>
                        <p>
                            FitnessLogger does not guarantee that any particular exercise
                            programme, training method or activity will produce a particular
                            fitness, strength, health or performance result.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">6. Accuracy of Training Information</h3>
                        <p>
                            FitnessLogger allows you to manually enter training information.
                        </p>
                        <p>
                            You are responsible for ensuring that the information you enter
                            is accurate.
                        </p>
                        <p>
                            We do not guarantee that user-entered information is accurate,
                            complete or suitable for a particular purpose.
                        </p>
                        <p>
                            Progression charts, statistics and other calculations are
                            provided for informational and tracking purposes only.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">7. Free and Pro Plans</h3>
                        <p>FitnessLogger may provide two service levels:</p>

                        <h4 className="fl__section-subtitle">7.1 Free Tier</h4>
                        <p>
                            The Free Tier provides access to FitnessLogger subject to certain
                            limitations.
                        </p>
                        <p>
                            These limitations may include restrictions on the number of
                            exercises, training logs, history, features or other
                            functionality available to the user.
                        </p>
                        <p>
                            The specific limitations applicable to the Free Tier will be
                            displayed within the App or on the relevant product or
                            subscription page.
                        </p>
                        <p>
                            We may change Free Tier limitations from time to time, subject to
                            applicable law.
                        </p>

                        <h4 className="fl__section-subtitle">7.2 Pro Tier</h4>
                        <p>
                            The Pro Tier provides access to additional functionality and/or
                            removes limitations applicable to the Free Tier.
                        </p>
                        <p>
                            The Pro Tier is intended to provide unrestricted access to the
                            features included within the Pro subscription.
                        </p>
                        <p>
                            The features included in Pro will be clearly described at the
                            time of purchase.
                        </p>
                        <p>
                            &quot;Unlimited&quot; or &quot;no limitations&quot; means that
                            FitnessLogger does not intentionally impose the ordinary usage
                            limits applicable to the Free Tier. It does not mean that the
                            Service is technically or legally unlimited in every
                            circumstance, and reasonable safeguards may be applied to prevent
                            abuse, fraud, excessive automated usage or misuse of the Service.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">8. Subscriptions and Payments</h3>
                        <p>
                            If you purchase a Pro subscription, payment will be processed
                            through the applicable app store or payment provider, such as
                            Google Play.
                        </p>
                        <p>
                            The price, billing period, currency, applicable taxes, renewal
                            terms and other material subscription information will be
                            presented before you complete your purchase.
                        </p>
                        <p>
                            Subscriptions may be offered on a monthly, annual or other
                            recurring basis.
                        </p>
                        <p>
                            Unless otherwise stated at the time of purchase, a subscription
                            will automatically renew at the end of each billing period until
                            cancelled.
                        </p>
                        <p>
                            You are responsible for cancelling your subscription before the
                            next renewal period if you do not wish to be charged for the next
                            period.
                        </p>
                        <p>
                            For subscriptions purchased through Google Play, users can manage
                            or cancel their subscription through Google Play&apos;s
                            subscription management facilities. Google states that
                            uninstalling an app does not itself cancel a subscription.
                        </p>
                        <p>
                            FitnessLogger will not misrepresent the price, benefits, duration
                            or renewal conditions of a subscription.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">
                            9. Free Trials and Promotional Offers
                        </h3>
                        <p>
                            FitnessLogger may occasionally offer free trials, introductory
                            pricing, promotional pricing or other special offers.
                        </p>
                        <p>
                            Where a trial converts into a paid subscription, the applicable
                            duration, price and renewal terms will be clearly communicated
                            before the user accepts the offer.
                        </p>
                        <p>
                            If you do not wish to be charged following a trial, you must
                            cancel the subscription in accordance with the applicable
                            cancellation instructions before the trial expires.
                        </p>
                        <p>
                            Promotional offers may be subject to additional terms and
                            eligibility requirements.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">10. Cancellation of Pro</h3>
                        <p>
                            You may cancel your Pro subscription at any time using the
                            subscription management facilities provided by the applicable app
                            store or payment provider.
                        </p>
                        <p>
                            Unless applicable law or the relevant payment provider&apos;s
                            policies require otherwise, cancelling a subscription will
                            normally prevent future renewals but will not automatically
                            refund the current billing period.
                        </p>
                        <p>
                            For Google Play subscriptions, Google&apos;s general policy is
                            that cancellation normally leaves access active until the end of
                            the current billing period, although specific legal rights or
                            refund circumstances may apply.
                        </p>
                        <p>
                            Cancellation of Pro does not necessarily delete your
                            FitnessLogger account or training data.
                        </p>
                        <p>
                            After cancellation, your account may revert to the Free Tier at
                            the end of the applicable paid period.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">11. Refunds</h3>
                        <p>
                            Payments and refunds for subscriptions purchased through an app
                            store may be subject to the applicable app store&apos;s terms,
                            policies and refund procedures.
                        </p>
                        <p>
                            Nothing in these Terms excludes or limits any refund or other
                            consumer right that cannot lawfully be excluded or limited.
                        </p>
                        <p>
                            Where applicable, you may request a refund through the platform
                            through which the purchase was made.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">12. Changes to Subscription Pricing</h3>
                        <p>
                            We may change Pro subscription prices from time to time.
                        </p>
                        <p>
                            Where required by law or the applicable app store&apos;s
                            policies, we will provide appropriate notice before a price
                            change takes effect.
                        </p>
                        <p>
                            A price change will not retroactively alter payments already
                            made.
                        </p>
                        <p>
                            If you do not wish to continue with a subscription following a
                            price change, you may cancel it in accordance with the applicable
                            cancellation process.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">13. Changes to the Service</h3>
                        <p>
                            We may modify, improve, add or remove features from FitnessLogger
                            from time to time.
                        </p>
                        <p>
                            We may also temporarily suspend certain features for maintenance,
                            security, technical or operational reasons.
                        </p>
                        <p>
                            We will make reasonable efforts to maintain the core
                            functionality of the Service, but we do not guarantee that every
                            feature will always remain available.
                        </p>
                        <p>
                            Where a change materially affects a paid service, we will take
                            reasonable steps to communicate the change where required by
                            applicable law.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">14. Data Storage and Availability</h3>
                        <p>We aim to store user data securely and reliably.</p>
                        <p>
                            However, you acknowledge that no online service can guarantee
                            that data will never be lost, corrupted, unavailable or
                            inaccessible.
                        </p>
                        <p>
                            We recommend that you retain copies of any information that is
                            particularly important to you where appropriate.
                        </p>
                        <p>
                            Our data collection, storage, retention and deletion practices
                            are described in our Privacy Policy.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">15. Account Deletion</h3>
                        <p>
                            You may request deletion of your FitnessLogger account and
                            associated personal data.
                        </p>
                        <p>Where account deletion is available, users can initiate deletion:</p>
                        <ul className="fl__list">
                            <li>Through the FitnessLogger App; and</li>
                            <li>
                                Through the external account deletion facility provided on
                                the FitnessLogger website.
                            </li>
                        </ul>
                        <p>
                            When an account is deleted, associated user data will be deleted
                            in accordance with our Privacy Policy, subject to information
                            that we are legally required or permitted to retain.
                        </p>
                        <p>
                            Deleting your FitnessLogger account does not necessarily cancel a
                            subscription purchased through an app store.
                        </p>
                        <p>
                            You should cancel any active subscription separately through the
                            applicable subscription management system.
                        </p>
                        <p>
                            Google Play requires apps that permit account creation to provide
                            account deletion both within the app and through an external web
                            resource.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">16. Acceptable Use</h3>
                        <p>You agree not to:</p>
                        <ul className="fl__list">
                            <li>Use FitnessLogger for unlawful purposes.</li>
                            <li>
                                Attempt to gain unauthorised access to another user&apos;s
                                account.
                            </li>
                            <li>
                                Attempt to access, modify or interfere with our systems.
                            </li>
                            <li>
                                Reverse engineer, decompile or disassemble the Service except
                                where permitted by applicable law.
                            </li>
                            <li>
                                Circumvent subscription restrictions or technical safeguards.
                            </li>
                            <li>
                                Use automated systems to abuse or overload the Service.
                            </li>
                            <li>Upload malicious code or harmful content.</li>
                            <li>Impersonate another person or entity.</li>
                            <li>
                                Use the Service to infringe the rights of another person.
                            </li>
                            <li>
                                Attempt to obtain Pro features without purchasing or
                                otherwise legitimately receiving access to them.
                            </li>
                        </ul>
                        <p>
                            We reserve the right to restrict or suspend access where
                            reasonably necessary to protect the Service, our users or third
                            parties.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">17. User Content</h3>
                        <p>
                            You are responsible for information, notes and other content that
                            you submit to FitnessLogger.
                        </p>
                        <p>
                            You confirm that you have the right to submit such content and
                            that your content does not knowingly:
                        </p>
                        <ul className="fl__list">
                            <li>Violate applicable law.</li>
                            <li>Infringe intellectual property rights.</li>
                            <li>Contain malicious software.</li>
                            <li>Attempt to compromise the security of the Service.</li>
                        </ul>
                        <p>
                            You grant us only the rights reasonably necessary to store,
                            process, display and provide your submitted content as part of
                            the Service.
                        </p>
                        <p>
                            We do not claim ownership of your workout records simply because
                            you store them in FitnessLogger.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">18. Intellectual Property</h3>
                        <p>
                            FitnessLogger and its underlying technology, software, design,
                            branding, logos, graphics, text and other materials are owned by
                            or licensed to FitnessLogger and are protected by applicable
                            intellectual property laws.
                        </p>
                        <p>
                            Except as expressly permitted by these Terms, you may not:
                        </p>
                        <ul className="fl__list">
                            <li>Copy FitnessLogger or substantial parts of it.</li>
                            <li>Reproduce our branding or visual designs.</li>
                            <li>
                                Modify or create derivative works based on the Service.
                            </li>
                            <li>
                                Distribute or commercially exploit the Service without our
                                permission.
                            </li>
                            <li>Remove proprietary notices.</li>
                        </ul>
                        <p>
                            Your use of FitnessLogger does not transfer ownership of our
                            intellectual property to you.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">19. Third-Party Services</h3>
                        <p>
                            FitnessLogger may use third-party services to provide
                            functionality such as:
                        </p>
                        <ul className="fl__list">
                            <li>Cloud hosting</li>
                            <li>Database storage</li>
                            <li>Authentication</li>
                            <li>Payment processing</li>
                            <li>Analytics</li>
                            <li>Crash reporting</li>
                            <li>Infrastructure and security</li>
                        </ul>
                        <p>
                            Third-party services may have their own terms and privacy
                            policies.
                        </p>
                        <p>
                            Your use of third-party services may be subject to those
                            providers&apos; terms in addition to these Terms.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">20. App Store Terms</h3>
                        <p>
                            If you download FitnessLogger through Google Play or another app
                            store, your use of the app may also be subject to the terms and
                            policies of that platform.
                        </p>
                        <p>
                            In the event of a conflict between these Terms and mandatory app
                            store requirements, the applicable mandatory requirements will
                            prevail to the extent of the conflict.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">21. Suspension and Termination</h3>
                        <p>
                            We may suspend or terminate your access to FitnessLogger where
                            reasonably necessary, including where:
                        </p>
                        <ul className="fl__list">
                            <li>You materially breach these Terms.</li>
                            <li>You engage in fraudulent or abusive activity.</li>
                            <li>Your use creates a security risk.</li>
                            <li>Required by law.</li>
                            <li>
                                Continued provision of the Service is no longer commercially
                                or technically viable.
                            </li>
                        </ul>
                        <p>
                            Where appropriate and reasonably possible, we will provide notice
                            before termination or suspension.
                        </p>
                        <p>
                            Termination does not affect rights or obligations that arose
                            before termination.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">22. Disclaimer of Warranties</h3>
                        <p>
                            To the maximum extent permitted by applicable law, FitnessLogger
                            is provided on an &quot;as available&quot; basis.
                        </p>
                        <p>We do not guarantee that:</p>
                        <ul className="fl__list">
                            <li>The Service will always be available.</li>
                            <li>The Service will be completely error-free.</li>
                            <li>
                                The Service will meet every user&apos;s individual
                                requirements.
                            </li>
                            <li>
                                Workout information will always be available without
                                interruption.
                            </li>
                            <li>Data will never be lost or corrupted.</li>
                            <li>
                                Particular fitness or performance results will be achieved.
                            </li>
                        </ul>
                        <p>
                            Nothing in these Terms excludes statutory rights or protections
                            that cannot legally be excluded.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">23. Limitation of Liability</h3>
                        <p>
                            To the maximum extent permitted by applicable law, FitnessLogger
                            will not be responsible for indirect, incidental, special or
                            consequential losses arising from your use of the Service.
                        </p>
                        <p>
                            Nothing in these Terms excludes or limits liability where doing
                            so would be unlawful, including liability for death or personal
                            injury caused by negligence, fraud or fraudulent
                            misrepresentation, or any other liability that cannot legally be
                            excluded or limited.
                        </p>
                        <p>
                            Where permitted by law, our total liability arising from your use
                            of FitnessLogger will be limited to the amount you paid to us for
                            the Service during the applicable period preceding the event
                            giving rise to the claim.
                        </p>
                        <p>
                            Nothing in this section affects your statutory consumer rights.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">24. Indemnity</h3>
                        <p>
                            To the extent permitted by applicable law, you agree to
                            compensate FitnessLogger for reasonable losses, liabilities and
                            expenses arising from your unlawful use of the Service, your
                            material breach of these Terms, or your infringement of another
                            person&apos;s rights.
                        </p>
                        <p>
                            This section does not apply to the extent that the relevant loss
                            was caused by our own negligence, breach of contract or unlawful
                            conduct.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">25. Privacy</h3>
                        <p>Your privacy is important to us.</p>
                        <p>
                            Our Privacy Policy explains how we collect, use, store, protect
                            and delete personal information.
                        </p>
                        <p>
                            The Privacy Policy can be found at:{' '}
                            <Link to="/fitnesslogger/policy">Privacy Policy</Link>
                        </p>
                        <p>
                            You should read the Privacy Policy before using FitnessLogger.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">26. Changes to These Terms</h3>
                        <p>We may update these Terms from time to time.</p>
                        <p>
                            If we make material changes, we will take reasonable steps to
                            notify users where appropriate.
                        </p>
                        <p>
                            The updated Terms will be made available through the App or our
                            website and will include an updated &quot;Last Updated&quot;
                            date.
                        </p>
                        <p>
                            Your continued use of FitnessLogger after the updated Terms take
                            effect constitutes acceptance of the revised Terms, to the extent
                            permitted by applicable law.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">27. Governing Law</h3>
                        <p>
                            These Terms are governed by the laws of{' '}
                            <strong>England and Wales</strong>, unless applicable consumer
                            protection law in your country gives you rights that cannot be
                            excluded by this choice of law.
                        </p>
                        <p>
                            Nothing in these Terms prevents a consumer from exercising
                            mandatory rights available under the laws of their country of
                            residence.
                        </p>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">28. Contact Us</h3>
                        <p>
                            If you have questions about these Terms, your account,
                            subscriptions or FitnessLogger, please contact us:
                        </p>
                        <ul className="fl__list">
                            <li>Developer/Company: Solo Pixel Dev</li>
                            <li>Email: contact@solopixeldev.co.uk</li>
                            <li>Website: solopixeldev.co.uk</li>
                            <li>
                                Privacy Policy:{' '}
                                <Link to="/fitnesslogger/policy">Privacy Policy</Link>
                            </li>
                            <li>
                                Account Deletion:{' '}
                                <Link to="/fitnesslogger/delete">Delete Account</Link>
                            </li>
                        </ul>
                    </section>

                    <section className="fl__section">
                        <h3 className="fl__section-title">29. Entire Agreement</h3>
                        <p>
                            These Terms, together with our Privacy Policy and any additional
                            terms specifically presented to you when using particular
                            features or purchasing a subscription, constitute the agreement
                            between you and FitnessLogger concerning your use of the Service.
                        </p>
                        <p>
                            If any provision of these Terms is found to be invalid or
                            unenforceable, the remaining provisions will continue to apply.
                        </p>
                        <p>
                            <strong>End of Terms &amp; Conditions</strong>
                        </p>
                    </section>
                </div>

                <hr className="fl__divider" />
            </main>
        </div>
    );
}
